
import React from "react";
import { motion } from "framer-motion";
import { Calendar } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { ClassType } from "@/lib/supabase/types";
import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";

interface ClassDetailsCardProps {
  classDetails: ClassType | undefined;
  isLoading: boolean;
}

export const ClassDetailsCard: React.FC<ClassDetailsCardProps> = ({ classDetails, isLoading }) => {
  const { t } = useTranslation();
  
  // Format date if available - just showing MMM d format to avoid day duplication
  const formattedDate = classDetails?.date 
    ? format(parseISO(classDetails.date), "MMM d")
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="mb-6 p-5 border border-border rounded-xl bg-card"
    >
      <h3 className="text-lg font-display font-medium mb-3">{t('booking.form.title')}</h3>
      {isLoading ? (
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('classes.details.class')}:</span>
            <span className="w-24 h-4 bg-muted animate-pulse rounded"></span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('classes.details.day')}:</span>
            <span className="w-20 h-4 bg-muted animate-pulse rounded"></span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('classes.details.time')}:</span>
            <span className="w-16 h-4 bg-muted animate-pulse rounded"></span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('classes.details.duration')}:</span>
            <span className="w-24 h-4 bg-muted animate-pulse rounded"></span>
          </div>
        </div>
      ) : classDetails ? (
        <div className="text-sm space-y-2">
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('classes.details.class')}:</span>
            <span className="font-medium">Pottery Class</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{t('classes.details.day')}:</span>
            <div className="flex items-center">
              <Calendar className="w-3.5 h-3.5 mr-1.5 text-primary/70" />
              <span>
                {classDetails.day}
                {formattedDate && `, ${formattedDate}`}
              </span>
            </div>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('classes.details.time')}:</span>
            <span>{classDetails.time}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('classes.details.duration')}:</span>
            <span>{classDetails.duration}</span>
          </div>
          <Separator className="my-2" />
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('classes.details.price')}:</span>
            <span className="font-medium">{classDetails.price} AMD</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('classes.details.available')}:</span>
            <span className="font-medium">
              {classDetails.availableSpots > 0 ? (
                <span className="text-green-600">
                  {t('classes.details.availableSpots', {
                    available: classDetails.availableSpots,
                    capacity: classDetails.capacity
                  })}
                </span>
              ) : (
                <span className="text-red-500">Class Full</span>
              )}
            </span>
          </div>
        </div>
      ) : (
        <div className="text-center text-sm text-muted-foreground py-2">
          {t('classes.noClassesDesc')}
        </div>
      )}
    </motion.div>
  );
};
